import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { morphSpringSoft } from '@/lib/motion';

import { VESTA_CONTACT_EMAIL } from '@/brand/vesta-cfo-brand';

const CONTACT_EMAIL = VESTA_CONTACT_EMAIL;

export function ContactForm() {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    property: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast({
        title: 'Missing fields',
        description: 'Please add your name, email, and message.',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    const subject = encodeURIComponent(
      form.property.trim()
        ? `Contact from ${form.name.trim()} — ${form.property.trim()}`
        : `Contact from ${form.name.trim()}`
    );
    const body = encodeURIComponent(
      `Name: ${form.name.trim()}\nEmail: ${form.email.trim()}\nProperty: ${form.property.trim() || '(not provided)'}\n\n${form.message.trim()}`
    );

    const mailto = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    window.location.href = mailto;

    toast({
      title: 'Message ready to send',
      description: 'Your email app should open with your message pre-filled. Send it from there to reach our team.',
    });
    setSubmitting(false);
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={morphSpringSoft}
      className="relative max-w-5xl mx-auto px-6 pb-12"
    >
      <div className="rounded-3xl border border-vesta-navy/10 bg-white shadow-lg shadow-vesta-navy/5 p-8 sm:p-10">
        <h2 className="font-serif text-2xl sm:text-3xl text-vesta-navy mb-2">Send us a message</h2>
        <p className="text-vesta-navy/75 text-sm mb-8 max-w-xl">
          Tell us about your property or question. We typically reply within one business day — no Calendly required.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5 max-w-xl">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="contact-name">Name</Label>
              <Input
                id="contact-name"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Your name"
                required
                autoComplete="name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-email">Email</Label>
              <Input
                id="contact-email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="you@hotel.com"
                required
                autoComplete="email"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="contact-property">Property or portfolio (optional)</Label>
            <Input
              id="contact-property"
              name="property"
              value={form.property}
              onChange={handleChange}
              placeholder="Hotel name, city, room count"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="contact-message">Message</Label>
            <Textarea
              id="contact-message"
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder="What would you like help with? PMS, pricing, demo, partnership…"
              rows={5}
              required
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="bg-vesta-gold hover:bg-vesta-gold/90 text-vesta-navy font-semibold gap-2"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            Send message
          </Button>
          <p className="text-xs text-vesta-navy/60">
            Submitting opens your email app with this message addressed to {CONTACT_EMAIL}.
          </p>
        </form>
      </div>
    </motion.section>
  );
}
